<template>
  <Tree
    :title="props.title"
    :items="props.items"
  >
    <!-- the text card -->
    <template #left="{ content: item, index }">
      <div
        class="relative rounded-md shadow-lg border border-gray-200 bg-white my-auto"
      >
        <section
          class="flex flex-col gap-2 m-card-lg"
        >
          <div
            class="flex flex-row gap-4"
          >
            <div
              class="flex bg-primary rounded-lg aspect-square h-[3em] w-[3em] mobile:hidden"
            >
              <font-awesome-icon
                class="text-white text-[2em] m-auto"
                :icon="props.items[index].icon"
              />
            </div>
            <h3
              class="text-slate-900 my-auto"
            >
              {{ item.title }}
            </h3>
          </div>
          <p
            class="text-slate-900"
          >
            {{ item.text }}
          </p>
        </section>
      </div>
    </template>

    <!-- the resources -->
    <template #right="{ content: item, index }">
      <div
        class="flex flex-col gap-4 justify-center items-center"
      >
        <div class="h-7" />
        <NuxtPicture
          v-if="(item as Content).render"
          v-show="!show[index] || show[index] === 'render'"
          :src="(item as Content).render!!.src"
          sizes="520px"
          class="m-auto w-full"
          :alt="(item as Content).render!!.alt"
        />
        <NuxtPicture
          v-if="(item as Content).image"
          v-show="(!show[index] && !(item as Content).render) || show[index] === 'image'"
          :src="(item as Content).image.src"
          sizes="400px tablet:512px laptop:384px desktop:444px"
          class="m-auto w-full rounded-md"
          :img-attrs="{ class: 'rounded-md' }"
          :alt="(item as Content).image.alt"
        />
        <video
          v-if="(item as Content).video"
          v-show="show[index] === 'video'"
          :src="(item as Content).video!!.src"
          class="m-auto w-full object-cover rounded"
          autoplay
          muted
          loop
          :alt="(item as Content).video!!.alt"
        />
        <div
          v-if="(item as Content).video || (item as Content).render"
          class="flex flex-row gap-3 py-1 px-4 bottom-0 my-auto bg-secondary text-white rounded-full"
        >
          <button
            v-if="(item as Content).render"
            class="aria-selected:text-primary"
            :aria-selected="!show[index] || show[index] == 'render'"
            type="button"
            @click="() => show[index] = 'render'"
          >
            <font-awesome-icon icon="sparkles" />
          </button>
          <button
            class="aria-selected:text-primary"
            :aria-selected="(!show[index] && !(item as Content).render) || show[index] == 'image'"
            type="button"
            @click="() => show[index] = 'image'"
          >
            <font-awesome-icon icon="image" />
          </button>
          <button
            v-if="(item as Content).video"
            class="aria-selected:text-primary"
            :aria-selected="show[index] == 'video'"
            type="button"
            @click="() => show[index] = 'video'"
          >
            <font-awesome-icon icon="film" />
          </button>
        </div>
      </div>
    </template>
  </Tree>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { Icon, Image, Video } from '~/utils/types'

interface Content {
  title: string
  text: string
  render?: Image
  image: Image
  video?: Video
}

interface TreeItem {
  id?: string
  icon: Icon
  content: Content
}

const props = defineProps<{
  title: string
  items: TreeItem[]
}>()

const show = ref<Array<string>>([])
</script>

<style scoped lang="postcss">
:deep(img) {
  @apply w-full h-full object-cover
}
</style>
